/**
 * Token utility functions for managing authentication tokens.
 * Supports JWT and Laravel Sanctum plain-text tokens (id|hash).
 */

const SANCTUM_TOKEN_TTL_MS = 24 * 60 * 60 * 1000; // matches backend sanctum expiration

const isJwtToken = (token) => typeof token === 'string' && token.split('.').length === 3;

const decodeJwtPayload = (token) => {
  if (!isJwtToken(token)) return null;

  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

export const setTokenTimestamp = () => {
  localStorage.setItem('token_created_at', String(Date.now()));
};

export const clearTokenTimestamp = () => {
  localStorage.removeItem('token_created_at');
};

const getSanctumExpirationTime = () => {
  const createdAt = localStorage.getItem('token_created_at');
  const issuedAt = createdAt ? parseInt(createdAt, 10) : Date.now();
  return issuedAt + SANCTUM_TOKEN_TTL_MS;
};

export const isTokenExpired = (token) => {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime + 300;
  }

  return Date.now() >= getSanctumExpirationTime() - 5 * 60 * 1000;
};

export const getTokenExpirationTime = (token) => {
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    return new Date(payload.exp * 1000);
  }

  return new Date(getSanctumExpirationTime());
};

export const getTimeUntilExpiration = (token) => {
  if (!token) return 0;

  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    const currentTime = Date.now() / 1000;
    return Math.max(0, (payload.exp - currentTime) * 1000);
  }

  return Math.max(0, getSanctumExpirationTime() - Date.now());
};

export const needsRefresh = (token) => {
  if (!token) return true;

  const timeUntilExpiration = getTimeUntilExpiration(token);
  const fourHours = 4 * 60 * 60 * 1000;

  return timeUntilExpiration < fourHours;
};

export const getTokenInfo = (token) => {
  if (!token) return null;

  const payload = decodeJwtPayload(token);

  return {
    type: payload ? 'jwt' : 'sanctum',
    issuedAt: payload?.iat ? new Date(payload.iat * 1000) : new Date(parseInt(localStorage.getItem('token_created_at') || Date.now(), 10)),
    expiresAt: getTokenExpirationTime(token),
    timeUntilExpiration: getTimeUntilExpiration(token),
    isExpired: isTokenExpired(token),
    needsRefresh: needsRefresh(token),
  };
};
